import {
  Box,
  Card,
  Grid,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import useGetYearOverYearRevenueReport from "hooks/organization/reports/types/useGetYearOverYearRevenueReport";
import useGetYearOverYearRevenueReportExport from "hooks/organization/reports/types/useGetYearOverYearRevenueReportExport";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

const currentYear = dayjs().year();
const lastYear = currentYear - 1;

const formatSignedPercent = (value: number) => `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
const calculatePercentDifference = (lastYearRevenue: number, thisYearRevenue: number) => {
  if (lastYearRevenue === 0) {
    return thisYearRevenue > 0 ? 100 : 0;
  }

  return ((thisYearRevenue - lastYearRevenue) / lastYearRevenue) * 100;
};

const getPercentColor = (value: number) => {
  if (value > 0) {
    return "success.main";
  }

  if (value < 0) {
    return "error.main";
  }

  return "text.primary";
};

export default function ReportsYearOverYearRevenue() {
  const { data, loading, error } = useGetYearOverYearRevenueReport();
  const [getYearOverYearRevenueReportExport, { data: exportMutationData }] =
    useGetYearOverYearRevenueReportExport();
  const downloadFileRef = useRef<HTMLAnchorElement | null>(null);
  const totalLastYearRevenue = data.reduce(
    (sum, row) => sum + Number(row.lastYearRevenue ?? 0),
    0
  );
  const totalThisYearRevenue = data.reduce(
    (sum, row) => sum + Number(row.thisYearRevenue ?? 0),
    0
  );
  const totalPercentDifference = calculatePercentDifference(
    totalLastYearRevenue,
    totalThisYearRevenue
  );

  useEffect(() => {
    const exportData = exportMutationData?.getYearOverYearRevenueReportExport;

    if (!exportData || !downloadFileRef.current) {
      return;
    }

    downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
      exportData
    )}`;
    downloadFileRef.current.download = "bitumio-year-over-year-revenue-report.xlsx";
    downloadFileRef.current.click();
  }, [exportMutationData]);

  return (
    <DashboardPage>
      <MDBox mb={6}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                p={3}
                lineHeight={1}
                display="flex"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Box>
                  <MDTypography variant="h4" fontWeight="medium">
                    Year over Year Revenue Report
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={1}>
                    Review won revenue by proposal won date for {currentYear} compared to {lastYear}.
                  </MDTypography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "info.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ color: "#fff" }}>bar_chart</Icon>
                </Box>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <MDTypography variant="h6">Won Revenue by Month</MDTypography>
                  <MDTypography variant="body2" color="text">
                    Full-year monthly comparison using proposal won date.
                  </MDTypography>
                </Box>
                <Box>
                  <a ref={downloadFileRef} className="d-none" />
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={() => getYearOverYearRevenueReportExport({ variables: {} })}
                  >
                    Download Excel
                  </MDButton>
                </Box>
              </MDBox>

              {loading ? (
                <MDBox p={3}>
                  <TableSkeleton numberOfLines={12} />
                </MDBox>
              ) : null}

              {error ? (
                <MDBox p={3}>
                  <MDAlert color="error">{error.message}</MDAlert>
                </MDBox>
              ) : null}

              {!loading && !error ? (
                <TableContainer>
                  <Table
                    sx={{
                      tableLayout: "fixed",
                      width: "100%",
                      "& .MuiTableHead-root": {
                        display: "table-header-group !important",
                      },
                      "& .MuiTableHead-root .MuiTableRow-root": {
                        display: "table-row !important",
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        display: "table-cell !important",
                      },
                      "& .MuiTableBody-root .MuiTableRow-root": {
                        display: "table-row !important",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell align="right">{lastYear} Revenue</TableCell>
                        <TableCell align="right">{currentYear} Revenue</TableCell>
                        <TableCell align="right">% Difference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((row) => (
                        <TableRow key={row.month}>
                          <TableCell>{row.month}</TableCell>
                          <TableCell align="right">
                            {formatCentsToUSD(Number(row.lastYearRevenue ?? 0))}
                          </TableCell>
                          <TableCell align="right">
                            {formatCentsToUSD(Number(row.thisYearRevenue ?? 0))}
                          </TableCell>
                          <TableCell align="right">
                            <MDTypography
                              variant="button"
                              fontWeight="regular"
                              color="text"
                              sx={{ color: getPercentColor(row.percentDifference) }}
                            >
                              {formatSignedPercent(row.percentDifference)}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="button" fontWeight="bold" color="dark">
                            Total
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="button" fontWeight="bold" color="dark">
                            {formatCentsToUSD(totalLastYearRevenue)}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography variant="button" fontWeight="bold" color="dark">
                            {formatCentsToUSD(totalThisYearRevenue)}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="right">
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                            sx={{ color: getPercentColor(totalPercentDifference) }}
                          >
                            {formatSignedPercent(totalPercentDifference)}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardPage>
  );
}
